import { useTranslation } from "react-i18next";
import type { TypedRecallFillContent } from "../content-types";
import { typedRecallCorrect } from "../grading";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Type the missing word(s) from memory — typo-tolerant per blank (mirrors iOS
 *  TypedRecallFillView + TypedRecallGrader). Answer is a per-blank string[]. */
export function TypedRecallFillRenderer({ content, answer, setAnswer, revealed }: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as TypedRecallFillContent;
  const blanks = c.blanks ?? [];
  const arr = (answer as string[]) ?? [];

  const setAt = (i: number, val: string) => {
    const next = [...arr];
    next[i] = val;
    setAnswer(next);
  };

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>
      {c.context_passage && (
        <div className="mt-3 whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm text-ink">
          {c.context_passage}
        </div>
      )}
      <div className="mt-4 space-y-3">
        {blanks.map((b, i) => {
          const val = arr[i] ?? "";
          const ok = revealed
            ? typedRecallCorrect(val, b.accepted_primary, b.accepted_alts ?? [], b.casing, b.umlaut_tolerance, b.max_typo_distance)
            : null;
          return (
            <div key={b.id}>
              <div className="mb-1 flex items-baseline gap-2 text-sm">
                <span className="font-semibold text-ink-faint">{i + 1}.</span>
                {b.hint && <span className="text-xs text-ink-faint">({b.hint})</span>}
              </div>
              <input
                type="text"
                value={val}
                disabled={revealed}
                onChange={(e) => setAt(i, e.target.value)}
                className={`w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 ${
                  revealed
                    ? ok
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-red-400 bg-red-50"
                    : "border-black/10 bg-white"
                }`}
              />
              {revealed && !ok && (
                <p className="mt-1 text-xs text-emerald-700">
                  {t("Correct:")} <span className="font-semibold">{b.accepted_primary}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
